import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";
import { KeyRound, RefreshCw, Search } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    BuildStatus,
    KeyHolder,
    PublicApiAccountRow,
    PublicApiOverview,
    getKeyHolders,
    getOwnerGroupStatus,
    getPublicApiAccounts,
    getPublicApiOverview,
    rebuildOwnerGroups,
    searchApiUsers,
    updatePublicApiAccount,
} from "interfaces/publicApi";
import { CallsTable, Card, FlagBadge, ProgressBar, REASON_LABELS, describeLimits, formatDateTime, formatDuration, formatRelative, n } from "./publicApi/components";

/**
 * /bo/public-api — the public API (/api-client): who has access, how much of the base each client
 * holds, every call (with what it returned), the suspicious ones, and the data behind
 * /api-client/owners. A client's own page is /bo/public-api/:userId.
 */

function dailyOptions(labels: string[]): ApexCharts.ApexOptions {
    return {
        chart: { type: "line", stacked: false, toolbar: { show: false }, fontFamily: "inherit", background: "transparent" },
        stroke: { width: [0, 0, 2], curve: "smooth" },
        plotOptions: { bar: { borderRadius: 2, columnWidth: "60%" } },
        colors: ["#1A1A1A", "#ef4444", "#16a34a"],
        dataLabels: { enabled: false },
        labels,
        xaxis: { type: "category", labels: { style: { colors: "#6b7280", fontSize: "10px" }, rotate: -45 }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: [
            { seriesName: "Appels", title: { text: "Appels", style: { color: "#6b7280", fontSize: "11px" } }, labels: { style: { colors: "#6b7280", fontSize: "11px" } } },
            { seriesName: "Appels", show: false },
            { opposite: true, seriesName: "Établissements livrés", title: { text: "Établissements livrés", style: { color: "#16a34a", fontSize: "11px" } }, labels: { style: { colors: "#16a34a", fontSize: "11px" } } },
        ],
        grid: { borderColor: "#f3f4f6", strokeDashArray: 3 },
        tooltip: { shared: true, intersect: false, y: { formatter: (v: number) => (v ?? 0).toLocaleString("fr-FR") } },
        legend: { position: "top", horizontalAlign: "left", fontSize: "12px", labels: { colors: "#374151" } },
    };
}

/**
 * "Clés et accès des clients": everyone who has a key, listed straight away, with every key and their
 * access — on by default, an admin can turn it off and on again here. Keys are created in
 * /bo/api-keys (links preselect the user); the search also finds users without a key.
 */
function GrantAccess({ onDone }: { onDone: () => void }) {
    const [holders, setHolders] = useState<KeyHolder[] | null>(null);
    const [q, setQ] = useState("");
    const [others, setOthers] = useState<Awaited<ReturnType<typeof searchApiUsers>>["users"]>([]);

    const loadHolders = () =>
        getKeyHolders()
            .then((r) => setHolders(r.holders))
            .catch(() => setHolders([]));
    useEffect(() => {
        loadHolders();
    }, []);

    // Users without any key, only when searching
    useEffect(() => {
        if (q.trim().length < 2) {
            setOthers([]);
            return;
        }
        const t = setTimeout(() => {
            searchApiUsers(q.trim())
                .then((r) => setOthers(r.users))
                .catch(() => setOthers([]));
        }, 300);
        return () => clearTimeout(t);
    }, [q]);

    const setAccess = async (h: KeyHolder, enabled: boolean) => {
        const question = enabled
            ? `Réactiver l'accès API de ${h.user.email} ?`
            : `Désactiver l'accès API de ${h.user.email} ?\nToutes ses clés seront refusées immédiatement (403), jusqu'à réactivation.`;
        if (!window.confirm(question)) return;
        const res = await updatePublicApiAccount(h.userId, { enabled });
        if (res.error) return window.alert(res.error);
        loadHolders();
        onDone();
    };

    const createLink = (id: string, email: string) => `/bo/api-keys?userId=${encodeURIComponent(id)}&email=${encodeURIComponent(email)}`;
    const needle = q.trim().toLowerCase();
    const shown = (holders || []).filter(
        (h) =>
            !needle ||
            [h.user.email, h.user.name, h.user.company, ...h.keys.flatMap((k) => [k.name, k.prefix])].some((v) => v && String(v).toLowerCase().includes(needle))
    );
    const holderIds = new Set((holders || []).map((h) => h.userId));
    const withoutKey = others.filter((u) => !holderIds.has(String(u._id)));
    const disabledCount = (holders || []).filter((h) => h.access === "disabled").length;

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col">
            <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-sm font-semibold text-gray-900">Clés et accès des clients</p>
                <Link to="/bo/api-keys" className="text-xs px-2.5 py-1 rounded bg-black text-white shrink-0">
                    Créer une clé
                </Link>
            </div>
            <p className="text-xs text-gray-500 mb-3">L'accès API est actif dès qu'une clé est créée. Désactivez-le ici à tout moment : toutes les clés du client sont alors refusées.</p>
            <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Filtrer, ou chercher un utilisateur sans clé…"
                    className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
            </div>
            {holders && (
                <p className="text-[11px] text-gray-500 mt-2">
                    {n(holders.length)} client{holders.length > 1 ? "s" : ""} avec une clé
                    {disabledCount > 0 && <span className="text-red-600"> · {n(disabledCount)} accès désactivé{disabledCount > 1 ? "s" : ""}</span>}
                </p>
            )}

            <div className="mt-2 max-h-[480px] overflow-y-auto -mx-1 px-1">
                {holders === null ? (
                    <p className="text-xs text-gray-400 py-4 text-center">Chargement…</p>
                ) : shown.length === 0 && withoutKey.length === 0 ? (
                    <p className="text-xs text-gray-400 py-4 text-center">{needle ? "Aucun utilisateur ne correspond." : "Aucun client n'a encore de clé : créez-en une."}</p>
                ) : (
                    <ul className="flex flex-col gap-2">
                        {shown.map((h) => (
                            <li key={h.userId} className={`border rounded-lg p-2.5 text-xs ${h.access === "disabled" ? "border-red-100 bg-red-50/30" : "border-gray-100"}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="text-gray-900 font-medium truncate" title={h.user.email}>
                                            {h.user.email}
                                        </p>
                                        {(h.user.company || h.user.name) && <p className="text-gray-500 truncate">{[h.user.name, h.user.company].filter(Boolean).join(" · ")}</p>}
                                        <div className="mt-1">
                                            {h.access === "enabled" ? (
                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 border border-green-100">Accès actif</span>
                                            ) : (
                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-100">Accès désactivé</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                        {h.access === "enabled" ? (
                                            <button onClick={() => setAccess(h, false)} className="px-2.5 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50">
                                                Désactiver
                                            </button>
                                        ) : (
                                            <button onClick={() => setAccess(h, true)} className="px-2.5 py-1 rounded bg-black text-white">
                                                Réactiver
                                            </button>
                                        )}
                                        <div className="flex gap-2">
                                            <Link to={createLink(h.userId, h.user.email)} className="text-gray-500 hover:text-gray-900 underline">
                                                + clé
                                            </Link>
                                            <Link to={`/bo/public-api/${h.userId}`} className="text-gray-500 hover:text-gray-900 underline">
                                                Voir
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                                <ul className="mt-2 border-t border-gray-50 pt-1.5 flex flex-col gap-1">
                                    {h.keys.map((k) => (
                                        <li key={k._id} className={`flex items-center justify-between gap-2 ${k.revokedAt ? "text-gray-400" : "text-gray-700"}`}>
                                            <span className="min-w-0 truncate" title={`${k.name} — ${k.prefix}…`}>
                                                <span className={k.revokedAt ? "line-through" : "font-medium"}>{k.name}</span> <span className="font-mono text-gray-400">{k.prefix}…</span>
                                            </span>
                                            <span className="shrink-0 text-[11px]" title={k.lastUsedIp ? `Dernière IP : ${k.lastUsedIp}` : undefined}>
                                                {k.revokedAt
                                                    ? `${k.revokedBy === "user" ? "supprimée" : "désactivée"} ${formatRelative(k.revokedAt).toLowerCase()}`
                                                    : k.lastUsedAt
                                                    ? `${n(k.usageCount)} appel${k.usageCount > 1 ? "s" : ""} · ${formatRelative(k.lastUsedAt).toLowerCase()}`
                                                    : `jamais utilisée · créée ${formatRelative(k.createdAt).toLowerCase()}`}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                        {withoutKey.length > 0 && (
                            <>
                                <li className="text-[11px] text-gray-400 pt-1">Sans clé API</li>
                                {withoutKey.map((u) => (
                                    <li key={u._id} className="flex items-center justify-between border border-dashed border-gray-200 rounded-lg px-2.5 py-2 text-xs">
                                        <div className="min-w-0">
                                            <p className="text-gray-900 truncate">{u.email}</p>
                                            {(u.company || u.name) && <p className="text-gray-500 truncate">{[u.name, u.company].filter(Boolean).join(" · ")}</p>}
                                        </div>
                                        <Link to={createLink(String(u._id), u.email)} className="px-2.5 py-1 rounded bg-black text-white shrink-0">
                                            Créer une clé
                                        </Link>
                                    </li>
                                ))}
                            </>
                        )}
                    </ul>
                )}
            </div>
        </div>
    );
}

function DataPanel({ status, onRebuild }: { status: BuildStatus | null; onRebuild: () => void }) {
    const s = status?.active?.stats;
    const histogramOrder = ["2", "3", "4", "5", "6-10", "11-20", "21-50", "51-80", ">80"];
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                    <p className="text-sm font-semibold text-gray-900">Données servies par l'API</p>
                    <p className="text-xs text-gray-500">
                        Groupes de propriétaires (co-propriétaires fusionnés), recalculés chaque nuit à 04:15.
                        {status?.active && <> Dernier calcul : {formatDateTime(status.active.builtAt)} ({Math.round(status.active.durationMs / 1000)} s).</>}
                    </p>
                    {status?.lastRun?.error && <p className="text-xs text-red-600 mt-1">Dernier calcul en échec : {status.lastRun.error}</p>}
                </div>
                <button
                    onClick={onRebuild}
                    disabled={!!status?.running}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 shrink-0"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${status?.running ? "animate-spin" : ""}`} />
                    {status?.running ? "Calcul en cours…" : "Recalculer"}
                </button>
            </div>
            {!s ? (
                <p className="text-xs text-gray-400">Aucun calcul pour l'instant : l'API répond 503 sur /api-client/owners tant qu'il n'a pas eu lieu.</p>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <Stat label="Base (100 % = tout livré)" value={n(s.baseEstablishments)} sub="établissements servis" />
                        <Stat label="Groupes de propriétaires" value={n(s.groups)} sub={`${n(s.peopleInGroups)} personnes · ${n(s.establishmentsInGroups)} établ.`} />
                        <Stat label="Exclus (> 80 établ.)" value={n(s.excludedGroups)} sub={`${n(s.establishmentsInExcludedGroups)} établ. · ${n(s.groupPersons)} personnes > 80`} />
                        <Stat label="Franchises hors groupes" value={n(s.franchiseOnly)} sub={`sur ${n(s.franchiseEstablishments)} établ. franchisés`} />
                    </div>
                    <p className="text-xs font-medium text-gray-600 mb-2">Groupes par nombre d'établissements</p>
                    <div className="flex items-end gap-2 h-20">
                        {histogramOrder.map((k) => {
                            const v = s.sizeHistogram[k] || 0;
                            const max = Math.max(1, ...Object.values(s.sizeHistogram));
                            return (
                                <div key={k} className="flex-1 flex flex-col items-center gap-1">
                                    <div className={`w-full rounded-sm ${k === ">80" ? "bg-red-300" : "bg-[#1A1A1A]"}`} style={{ height: `${Math.max(2, (v / max) * 56)}px` }} title={`${k} établ. : ${v} groupes`} />
                                    <span className="text-[10px] text-gray-500">{k}</span>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

function Stat({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
    return (
        <div className="border border-gray-100 rounded-lg p-3">
            <p className="text-[11px] text-gray-500">{label}</p>
            <p className="text-lg font-semibold text-gray-900">{value}</p>
            {sub && <p className="text-[11px] text-gray-400">{sub}</p>}
        </div>
    );
}

export default function PublicApi() {
    const navigate = useNavigate();
    const [overview, setOverview] = useState<PublicApiOverview | null>(null);
    const [accounts, setAccounts] = useState<PublicApiAccountRow[]>([]);
    const [build, setBuild] = useState<BuildStatus | null>(null);
    const [q, setQ] = useState("");

    const loadAll = () => {
        getPublicApiOverview().then(setOverview).catch(() => setOverview(null));
        getOwnerGroupStatus().then(setBuild).catch(() => setBuild(null));
    };
    const loadAccounts = () =>
        getPublicApiAccounts(q.trim() || undefined)
            .then((r) => setAccounts(r.accounts))
            .catch(() => setAccounts([]));

    useEffect(() => {
        loadAll();
    }, []);
    useEffect(() => {
        const t = setTimeout(loadAccounts, q ? 300 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    // While a rebuild runs, poll its status
    useEffect(() => {
        if (!build?.running) return;
        const t = setInterval(() => getOwnerGroupStatus().then(setBuild).catch(() => {}), 5000);
        return () => clearInterval(t);
    }, [build?.running]);

    const rebuild = async () => {
        if (!window.confirm("Recalculer les groupes de propriétaires maintenant ? (quelques minutes)")) return;
        const res = await rebuildOwnerGroups();
        if (res.error) window.alert(res.error);
        setTimeout(() => getOwnerGroupStatus().then(setBuild).catch(() => {}), 1000);
    };

    const daily = overview?.daily || [];
    const base = build?.active?.stats.baseEstablishments || 0;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4 pb-12">
                <div className="mb-6 flex items-end justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">API publique</h1>
                        <p className="text-sm text-gray-500 mt-0.5">Clients, limitation des appels (réglée ici, invisible pour eux), part de la base reçue, chaque appel et ce qu'il a renvoyé, comportements suspects</p>
                    </div>
                    <Link to="/bo/api-keys" className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 underline">
                        <KeyRound className="w-3.5 h-3.5" />
                        Toutes les clés
                    </Link>
                </div>

                <BONavigation />

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <Card label="Clients avec accès" value={overview ? `${overview.accounts.enabled}` : "—"} sub={overview ? `${overview.accounts.total} comptes créés · ${overview.last24h.clients} actifs sur 24 h` : undefined} />
                    <Card
                        label="Appels (24 h)"
                        value={overview ? n(overview.last24h.calls) : "—"}
                        sub={overview ? `${n(overview.last24h.errors)} erreurs · ${n(overview.last24h.flagged)} signalés` : undefined}
                        tone={overview && overview.last24h.flagged > 0 ? "danger" : undefined}
                    />
                    <Card label="Établissements livrés (7 j)" value={overview ? n(overview.last7d.delivered) : "—"} sub={overview ? `${n(overview.last24h.delivered)} sur 24 h` : undefined} />
                    <Card label="Base servie" value={base ? n(base) : "—"} sub={build?.active ? `${n(build.active.stats.groups)} groupes de propriétaires` : "pas encore calculée"} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                    <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">30 derniers jours</p>
                        <p className="text-xs text-gray-400 mb-2">Appels par jour (dont erreurs) et établissements livrés pour la première fois</p>
                        <div className="h-64">
                            {daily.length === 0 ? (
                                <p className="text-xs text-gray-400 pt-10 text-center">Aucun appel sur la période.</p>
                            ) : (
                                <Chart
                                    type="line"
                                    width="100%"
                                    height="100%"
                                    options={dailyOptions(daily.map((d) => d.day.slice(5)))}
                                    series={[
                                        { name: "Appels", type: "column", data: daily.map((d) => d.calls - d.errors) },
                                        { name: "Erreurs", type: "column", data: daily.map((d) => d.errors) },
                                        { name: "Établissements livrés", type: "line", data: daily.map((d) => d.delivered) },
                                    ]}
                                />
                            )}
                        </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">Signaux (7 j)</p>
                        <p className="text-xs text-gray-400 mb-3">Comportements à surveiller, tous clients confondus. Rouge = tentative probable.</p>
                        {!overview?.flags.length ? (
                            <p className="text-xs text-gray-400">Rien à signaler.</p>
                        ) : (
                            <div className="flex flex-col gap-1.5">
                                {overview.flags.map((f) => (
                                    <div key={f.flag} className="flex items-center justify-between">
                                        <FlagBadge flag={f.flag} count={f.count} />
                                        <span className="text-[11px] text-gray-400">{formatRelative(f.lastAt)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">Clients</p>
                        <div className="relative w-72">
                            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer…" className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400" />
                        </div>
                    </div>
                    {accounts.length === 0 ? (
                        <p className="px-4 py-8 text-center text-xs text-gray-400">Aucun client n'a encore d'accès API.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-100 text-xs text-gray-500">
                                        <th className="text-left px-4 py-2 font-medium">Client</th>
                                        <th className="text-left px-4 py-2 font-medium">Statut</th>
                                        <th className="text-left px-4 py-2 font-medium w-48">Part de la base reçue</th>
                                        <th className="text-left px-4 py-2 font-medium">Limitation (invisible pour le client)</th>
                                        <th className="text-left px-4 py-2 font-medium">Maintenant</th>
                                        <th className="text-right px-4 py-2 font-medium">Livrés 7 j</th>
                                        <th className="text-right px-4 py-2 font-medium">Appels 24 h / 7 j</th>
                                        <th className="text-right px-4 py-2 font-medium">Signalés 7 j</th>
                                        <th className="text-left px-4 py-2 font-medium">Dernier appel</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accounts.map((a) => (
                                        <tr key={a.userId} onClick={() => navigate(`/bo/public-api/${a.userId}`)} className={`border-b border-gray-50 hover:bg-[#F9F6F3] cursor-pointer text-xs ${a.enabled ? "" : "opacity-60"}`}>
                                            <td className="px-4 py-2.5">
                                                <p className="text-sm text-gray-900">{a.user?.email || "Utilisateur supprimé"}</p>
                                                <p className="text-gray-500">{[a.user?.company, `${a.activeKeys} clé${a.activeKeys > 1 ? "s" : ""} active${a.activeKeys > 1 ? "s" : ""}`].filter(Boolean).join(" · ")}</p>
                                            </td>
                                            <td className="px-4 py-2.5">
                                                {a.enabled ? (
                                                    <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-green-50 text-green-700 border border-green-100">Actif</span>
                                                ) : (
                                                    <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">Désactivé</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <div className="flex items-center gap-2">
                                                    <ProgressBar percent={a.coverage} />
                                                    <span className="text-gray-900 font-medium w-14 text-right">{a.coverage.toLocaleString("fr-FR")} %</span>
                                                </div>
                                                <p className="text-[11px] text-gray-400 mt-0.5">
                                                    {n(a.delivered)} / {n(a.base)}
                                                </p>
                                            </td>
                                            <td className="px-4 py-2.5 text-gray-700 max-w-[260px]">
                                                <p className="truncate" title={describeLimits(a.limits)}>{describeLimits(a.limits)}</p>
                                                <p className="text-[11px] text-gray-400">≤ {n(a.estimate.callsPerDay)} appels/j{a.estimate.daysForBase ? ` · base entière en ≥ ${n(a.estimate.daysForBase)} j` : ""}</p>
                                            </td>
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {a.rate.nextCallAllowed ? (
                                                    <span className="text-green-700">Libre</span>
                                                ) : (
                                                    <span className="text-red-600" title={REASON_LABELS[a.rate.nextCallReason || ""]}>
                                                        Bloqué {formatDuration(a.rate.nextCallInSec)}
                                                    </span>
                                                )}
                                                <p className="text-[11px] text-gray-400">{n(a.rate.callsToday)} appels aujourd'hui</p>
                                            </td>
                                            <td className="px-4 py-2.5 text-right text-gray-900">{n(a.delivered7d)}</td>
                                            <td className="px-4 py-2.5 text-right text-gray-900">
                                                {n(a.calls24h)} / {n(a.calls7d)}
                                                {a.errors7d > 0 && <span className="block text-[11px] text-red-600">{n(a.errors7d)} erreurs</span>}
                                            </td>
                                            <td className={`px-4 py-2.5 text-right ${a.flagged7d > 0 ? "text-red-600 font-semibold" : "text-gray-400"}`}>{n(a.flagged7d)}</td>
                                            <td className="px-4 py-2.5 text-gray-600">{formatRelative(a.lastCallAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                    <GrantAccess
                        onDone={() => {
                            loadAccounts();
                            loadAll();
                        }}
                    />
                    <div className="lg:col-span-2">
                        <DataPanel status={build} onRebuild={rebuild} />
                    </div>
                </div>

                <CallsTable title="Tous les appels" />
            </div>
        </div>
    );
}
